import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Activity,
  AlarmClock,
  BellRing,
  Bug,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleHelp,
  Clock3,
  Copy,
  Cpu,
  Download,
  Eye,
  EyeOff,
  FileText,
  Fingerprint,
  FolderKanban,
  Globe,
  Gauge,
  Hash,
  HeartPulse,
  History,
  Info,
  KeyRound,
  ListOrdered,
  MessageSquare,
  Play,
  RefreshCcw,
  RotateCcw,
  Search,
  ShieldCheck,
  Sigma,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import API from "../../services/api";
import CustomSelect from "../../components/CustomSelect";
import PageModal from "../../components/PageModal";
import { useToast } from "../../components/Toast";
import {
  formatTimestamp,
  getAccountSetRequestTypeMeta,
  loadAdminAccountsData,
} from "./adminShared";
import { ACCOUNT_SET_STATUS_META, resolveAccountSetRowStatus } from "./accountSetStatus";
import {
  normalizeCollectorSetFilesResponse,
  buildCollectorSetSingleFileClipboardText,
  getCollectorSetBrokers,
  getCollectorSetDownloadFilename,
  normalizeCollectorSetFileResponse,
} from "./collectorSetFiles";
import "./AdminShared.css";
import "./AdminAccounts.css";

const REQUEST_CHANGE_FIELD_META = {
  name: {
    label: "Set name",
    scope: "set",
  },
  brokerName: {
    label: "Broker name",
    scope: "broker",
  },
  terminal: {
    label: "Terminal",
    scope: "broker",
  },
  server: {
    label: "Server",
    scope: "broker",
  },
  accountNumber: {
    label: "Account number",
    scope: "broker",
  },
  password: {
    label: "Password",
    scope: "broker",
    sensitive: true,
  },
  requestedSymbol: {
    label: "Requested symbol",
    scope: "broker",
  },
};

const REQUEST_CHANGE_BROKER_FIELDS = [
  "brokerName",
  "terminal",
  "server",
  "accountNumber",
  "password",
  "requestedSymbol",
];

const COLLECTOR_SET_GUIDE_CALLOUTS = [
  {
    title: "MT4 and MT5 use the same config shape",
    body: "The templates differ by terminal, but the field names and collector behavior are intentionally almost identical.",
  },
  {
    title: "MagicNumberFilter and CommentPrefix are AND filters",
    body: "If both are restrictive, a trade must match both. Example: MagicNumberFilter=123456 plus CommentPrefix=ppabc123 hides trades that only match one of them.",
  },
  {
    title: "Generated files always keep a tag",
    body: "Every generated collector file uses its own persistent CommentPrefix. Duplicate login means same terminal, server, and account number, and those files may additionally set AutoRegister=false.",
  },
  {
    title: "Closed-history recovery is on by default",
    body: "ClosedHistoryLookbackHours=168 lets MT4/MT5 recover tagged close events after an EA or backend restart without collecting manual user trades.",
  },
];

const COLLECTOR_SET_EXAMPLES = [
  {
    title: "Normal generated file",
    rows: [
      "EndpointBase=http://107.0.0.1:50000",
      "AutoRegister=true",
      "ClosedHistoryLookbackHours=168",
      "MagicNumberFilter=0",
      "CommentPrefix=ppabc123",
      "HelpFilters=HELP: This collector reports orders tagged with CommentPrefix=ppabc123",
    ],
  },
  {
    title: "Duplicate-login generated file",
    rows: [
      "EndpointBase=http://107.0.0.1:50000",
      "AutoRegister=false",
      "ClosedHistoryLookbackHours=168",
      "CommentPrefix=ppdef456",
      "Manual IDs stay in AccountSetId, BrokerId, and BrokerPosition",
      "HelpFilters explains that only ppdef456-tagged orders are reported",
    ],
  },
];

const COLLECTOR_SET_FIELD_GROUPS = [
  {
    title: "Required system identity",
    description:
      "These bind the collector to the backend and the correct account-set side. Treat generated values as read-only.",
    fields: [
      {
        name: "EndpointBase",
        label: "Backend URL",
        description: "Backend server URL used by this collector.",
        example: "http://127.0.0.1:5000",
        tags: ["Backend-controlled", "Dangerous"],
        warning: "Wrong value means the collector cannot connect.",
        tooltip:
          "The EA builds routes like /api/collector/register and /api/collector/tick from this URL.",
      },
      {
        name: "CollectorKey",
        label: "Collector auth key",
        description: "Authentication key used by the collector to connect securely to the backend.",
        example: "flux_collector_dev_key",
        tags: ["Sensitive", "Backend-controlled"],
        warning: "Keep masked in UI. A mismatch blocks all collector requests.",
        tooltip: "Sent with collector requests as the shared backend API key.",
      },
      {
        name: "BrokerKey",
        label: "Broker routing key",
        description: "Internal broker routing key used by the platform.",
        example: "vpfx",
        tags: ["Backend-generated"],
        tooltip: "Helps the backend map ticks and orders to the right broker stream.",
      },
      {
        name: "AccountSetId",
        label: "Account-set ID",
        description:
          "Internal account-set ID used to bind this collector to the correct linked account pair.",
        example: "8fe05126-8cd3-41a6-8a7d-76ef8d6ef2d2",
        tags: ["Read-only generated", "Dangerous"],
        warning: "Do not edit manually unless you are intentionally rebinding the collector.",
        tooltip: "Especially important when AutoRegister=false.",
      },
      {
        name: "BrokerId",
        label: "Broker ID",
        description: "Internal broker ID for this collector account.",
        example: "c0d9978f-e94e-46cd-bae8-d596d8a77450",
        tags: ["Read-only generated"],
        tooltip: "Identifies the exact broker row inside the account set.",
      },
      {
        name: "BrokerPosition",
        label: "Broker side",
        description: "Which side of the account set this broker represents.",
        example: "1 or 2",
        tags: ["Read-only generated"],
        tooltip: "Usually 1 or 2 so the backend can separate the paired accounts.",
      },
    ],
  },
  {
    title: "Performance and timing",
    description:
      "Hide these under Advanced in any editor. They control polling speed, idle behavior, and periodic status updates.",
    advanced: true,
    fields: [
      {
        name: "PollIntervalMs",
        label: "Active poll interval",
        description: "Base polling interval while the collector is active.",
        example: "50",
        tags: ["Advanced edit"],
        tooltip: "Lower values can feel faster but use more CPU and network.",
      },
      {
        name: "IdlePollIntervalMs",
        label: "Idle poll interval",
        description: "Polling interval used after the collector becomes idle.",
        example: "100",
        tags: ["Advanced edit"],
        tooltip: "Usually slower than active polling to reduce resource usage.",
      },
      {
        name: "MaxPollIntervalMs",
        label: "Max poll interval",
        description: "Maximum polling interval allowed during throttling or backoff.",
        example: "200",
        tags: ["Advanced edit"],
        tooltip: "Caps how slow polling can become during adaptive backoff.",
      },
      {
        name: "IdleTimeoutSec",
        label: "Idle timeout",
        description: "How long the collector waits before switching from active mode to idle mode.",
        example: "5",
        tags: ["Advanced edit"],
        tooltip: "Works with IdlePollIntervalMs.",
      },
      {
        name: "MaxCpuUsagePercent",
        label: "CPU throttle limit",
        description: "CPU usage threshold for adaptive polling.",
        example: "20.0",
        tags: ["Advanced edit"],
        warning: "Only matters when EnableAdaptivePolling=true.",
        tooltip: "Adaptive polling may slow down when estimated CPU usage crosses this value.",
      },
      {
        name: "EnableAdaptivePolling",
        label: "Adaptive polling",
        description: "Automatically adjust polling speed based on load and activity.",
        example: "false",
        tags: ["Advanced edit"],
        tooltip: "When false, the collector uses the normal fixed polling flow.",
      },
      {
        name: "HeartbeatSec",
        label: "Heartbeat interval",
        description: "How often the collector reports that it is alive.",
        example: "10",
        tags: ["Advanced edit"],
        tooltip: "If <= 0, heartbeat sending is effectively disabled.",
      },
      {
        name: "SymbolSpecsSec",
        label: "Symbol metadata refresh",
        description: "How often symbol metadata is refreshed and sent to the backend.",
        example: "600",
        tags: ["Advanced edit"],
        tooltip: "If <= 0, symbol spec refresh is effectively disabled.",
      },
      {
        name: "ClosedHistoryLookbackHours",
        label: "Closed-history lookback",
        description:
          "How far back the collector scans closed trades after startup or reload to recover missed close reports.",
        example: "168",
        tags: ["Operational", "Recovery"],
        warning:
          "Keep the default unless recovering older missed closes; it still only reports trades matching CommentPrefix and MagicNumberFilter.",
        tooltip:
          "168 hours is seven days. Useful when the EA or backend was restarted after tagged trades had already closed.",
      },
    ],
  },
  {
    title: "Trade filters",
    description:
      "These actively decide which orders are reported. Keep them visible in review, but hide editing behind Advanced.",
    advanced: true,
    fields: [
      {
        name: "MagicNumberFilter",
        label: "Magic number filter",
        description:
          "Only track trades with this MT magic number. Use 0 to track all magic numbers.",
        example: "0 or 123456",
        tags: ["Advanced edit", "Risky"],
        warning: "Applied together with CommentPrefix. A trade must satisfy both filters.",
        tooltip: "Positive values narrow order reporting to one magic number.",
      },
      {
        name: "CommentPrefix",
        label: "Order comment prefix",
        description:
          "Only track trades whose order comment starts with this backend-generated broker tag.",
        example: "ppabc123",
        tags: ["Backend-generated", "Risky"],
        warning:
          "Generated files should keep this non-empty value aligned with backend order comments. It is ANDed with MagicNumberFilter.",
        tooltip:
          "Every generated file uses a persistent tag so collectors only report that broker binding’s trades.",
      },
    ],
  },
  {
    title: "Logging and debug",
    description:
      "Useful for troubleshooting. Keep verbose logging behind Advanced because it can produce a lot of output.",
    advanced: true,
    fields: [
      {
        name: "LogDebug",
        label: "Debug logs",
        description: "Enable standard debug logs.",
        example: "true",
        tags: ["Safe advanced edit"],
        tooltip: "Useful for setup and connectivity troubleshooting.",
      },
      {
        name: "VerboseLog",
        label: "Verbose logs",
        description: "Enable extra detailed logs for advanced troubleshooting.",
        example: "false",
        tags: ["Advanced edit"],
        warning: "Can generate a lot of log output.",
        tooltip: "Turn on only while diagnosing deeper collector issues.",
      },
    ],
  },
  {
    title: "Execution and registration",
    description:
      "These control whether the collector can execute backend commands and how it binds itself to the account set.",
    fields: [
      {
        name: "EnableExecution",
        label: "Allow execution",
        description: "Allow this collector to receive and execute commands from the backend.",
        example: "true",
        tags: ["Operational", "Dangerous"],
        warning: "Turning it off makes the collector read-only for server-driven execution.",
        tooltip: "When false, the collector does not poll /api/collector/commands/poll.",
      },
      {
        name: "AutoRegister",
        label: "Auto-register collector",
        description:
          "Automatically register this collector with the backend using terminal login details.",
        example: "true or false",
        tags: ["Backend-controlled", "Risky"],
        warning:
          "Duplicate-login files may intentionally set this to false and rely on manual IDs.",
        tooltip:
          "When false, AccountSetId, BrokerId, and BrokerPosition become the binding source.",
      },
      {
        name: "AutoRegisterRetrySec",
        label: "Registration retry",
        description: "How long to wait before retrying auto-registration after a failure.",
        example: "10",
        tags: ["Advanced edit"],
        tooltip: "The EA enforces a minimum retry floor internally.",
      },
      {
        name: "AutoRegisterRefreshSec",
        label: "Registration refresh",
        description: "How often to refresh an existing auto-registration.",
        example: "300",
        tags: ["Advanced edit"],
        tooltip: "Periodically revalidates the registration state.",
      },
    ],
  },
  {
    title: "Informational help",
    description: "These fields explain the file. They are not runtime switches.",
    fields: [
      {
        name: "HelpFilters",
        label: "Filter help text",
        description: "Informational helper text for filter usage.",
        example:
          "HELP: Duplicate login detected. AutoRegister is disabled. This collector only reports orders tagged with CommentPrefix=ppdef456",
        exampleLabel: "Shown note",
        exampleStyle: "text",
        tags: ["Help-only", "Backend-customized"],
        warning: "Do not treat this as a real config toggle.",
        tooltip: "Shown as an EA input note only.",
      },
    ],
  },
];

const IMPORTANT_COLLECTOR_SET_KEYS = new Set([
  "EndpointBase",
  "CollectorKey",
  "BrokerKey",
  "AccountSetId",
  "BrokerId",
  "BrokerPosition",
  "RequestedSymbol",
  "ClosedHistoryLookbackHours",
  "MagicNumberFilter",
  "CommentPrefix",
  "EnableExecution",
  "AutoRegister",
]);

function getCollectorGuideTagClass(tag) {
  const lower = String(tag || "").toLowerCase();
  if (lower.includes("danger") || lower.includes("sensitive") || lower.includes("risky")) {
    return "admin-accounts-config-tag-danger";
  }
  if (lower.includes("advanced") || lower.includes("operational")) {
    return "admin-accounts-config-tag-warning";
  }
  if (lower.includes("help")) {
    return "admin-accounts-config-tag-neutral";
  }
  return "admin-accounts-config-tag-info";
}

function buildAccountSetRow(accountSet, rowStatus) {
  const resolvedRowStatus = resolveAccountSetRowStatus(accountSet, rowStatus);
  const brokers = Array.isArray(accountSet?.brokers) ? accountSet.brokers : [];
  const setId = accountSet?.id || accountSet?._id || "";
  const setName = accountSet?.name || "Unnamed Set";
  const userEmail = accountSet?.User?.email || "Unknown";
  const requestedFutureSymbol = accountSet?.requestedFutureSymbol || "";
  const requestedSpotSymbol = accountSet?.requestedSpotSymbol || "";
  const createdLabel = formatTimestamp(accountSet?.createdAt);
  const deletedLabel = accountSet?.deletedAt ? formatTimestamp(accountSet.deletedAt) : "—";
  const brokerIds = brokers.map((broker, index) => {
    const brokerPosition = broker?.position || index + 1;
    const brokerIdValue = broker?.id || broker?._id || "";
    const brokerLabel = broker?.brokerName || broker?.server || `Broker ${brokerPosition}`;

    return {
      key: String(brokerIdValue || `${setId}-${brokerPosition}`),
      label: `${brokerLabel} ID`,
      value: brokerIdValue || "—",
      copyLabel: `${brokerLabel} ID`,
    };
  });
  const brokerInfoRows = brokers.map((broker, index) => {
    const brokerPosition = broker?.position || index + 1;
    const brokerIdValue = broker?.id || broker?._id || "";

    return {
      key: String(brokerIdValue || `${setId}-${brokerPosition}`),
      id: brokerIdValue || "",
      position: brokerPosition,
      terminal: broker?.terminal || "",
      brokerName: broker?.brokerName || broker?.name || "",
      server: broker?.server || "",
      accountNumber: broker?.accountNumber || "",
      password: broker?.password || "",
      requestedSymbol: broker?.requestedSymbol || "",
    };
  });
  const searchText = [
    setId,
    setName,
    userEmail,
    accountSet?.futureSymbol,
    accountSet?.spotSymbol,
    resolvedRowStatus,
    ...brokers.flatMap((broker) => [
      broker?.position,
      broker?.terminal,
      broker?.brokerName,
      broker?.server,
      broker?.accountNumber,
      broker?.id,
      broker?._id,
    ]),
  ]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");

  return {
    ...accountSet,
    rowStatus: resolvedRowStatus,
    setId,
    setName,
    userEmail,
    requestedFutureSymbol,
    requestedSpotSymbol,
    createdLabel,
    deletedLabel,
    brokerIds,
    brokerInfoRows,
    collectorSetBrokers: getCollectorSetBrokers(accountSet),
    searchText,
  };
}

function getRequestSnapshotBrokers(request) {
  const details = request?.details || {};
  if (Array.isArray(details.brokers) && details.brokers.length > 0) {
    return [...details.brokers].sort((a, b) => (a?.position || 0) - (b?.position || 0));
  }

  const accountSetBrokers = Array.isArray(request?.AccountSet?.brokers)
    ? request.AccountSet.brokers
    : [];
  return [...accountSetBrokers].sort((a, b) => (a?.position || 0) - (b?.position || 0));
}

function getBrokerDisplayValue(value) {
  const text = String(value ?? "").trim();
  return text || "-";
}

function maskBrokerPassword(password) {
  const text = String(password ?? "").trim();
  if (!text) return "-";
  return "*".repeat(Math.min(Math.max(text.length, 4), 12));
}

function parseCollectorSetPreviewContent(content) {
  return String(content || "")
    .split(/\r?\n/)
    .map((rawLine, index) => {
      const line = rawLine.trim();
      if (!line) return null;

      const separatorIndex = line.indexOf("=");
      if (separatorIndex <= 0) {
        return {
          id: `note-${index}`,
          type: "note",
          text: line,
        };
      }

      return {
        id: `entry-${index}`,
        type: "entry",
        key: line.slice(0, separatorIndex).trim(),
        important: IMPORTANT_COLLECTOR_SET_KEYS.has(line.slice(0, separatorIndex).trim()),
        value: line.slice(separatorIndex + 1).trim(),
      };
    })
    .filter(Boolean);
}

function isCollectorSetUuidValue(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );
}

const COLLECTOR_SET_FIELD_ICONS = {
  EndpointBase: Globe,
  CollectorKey: KeyRound,
  BrokerKey: ShieldCheck,
  AccountSetId: FolderKanban,
  BrokerId: Fingerprint,
  BrokerPosition: ListOrdered,
  PollIntervalMs: Clock3,
  IdlePollIntervalMs: AlarmClock,
  MaxPollIntervalMs: Gauge,
  IdleTimeoutSec: Clock3,
  MaxCpuUsagePercent: Cpu,
  EnableAdaptivePolling: Activity,
  HeartbeatSec: HeartPulse,
  SymbolSpecsSec: Sigma,
  ClosedHistoryLookbackHours: History,
  MagicNumberFilter: Hash,
  CommentPrefix: MessageSquare,
  LogDebug: Bug,
  VerboseLog: FileText,
  EnableExecution: Play,
  AutoRegister: UserPlus,
  AutoRegisterRetrySec: RotateCcw,
  AutoRegisterRefreshSec: RefreshCcw,
  HelpFilters: CircleHelp,
};

function getCollectorSetPreviewFieldIcon(key) {
  return COLLECTOR_SET_FIELD_ICONS[key] || Info;
}

function splitCollectorSetPreviewRows(rows = [], primaryCount = 11, secondaryCount = 11) {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const footerRows = normalizedRows.filter(
    (row) => row?.type === "entry" && row?.key === "HelpFilters",
  );
  const mainRows = normalizedRows.filter(
    (row) => !(row?.type === "entry" && row?.key === "HelpFilters"),
  );

  if (mainRows.length <= primaryCount) {
    return {
      primaryRows: mainRows,
      secondaryRows: [],
      footerRows,
    };
  }

  return {
    primaryRows: mainRows.slice(0, primaryCount),
    secondaryRows: mainRows.slice(primaryCount, primaryCount + secondaryCount),
    footerRows,
  };
}

function sortRequestBrokers(brokers = []) {
  return [...brokers].sort((a, b) => (a?.position || 0) - (b?.position || 0));
}

function normalizeRequestChangeComparableValue(field, value) {
  const text = String(value ?? "").trim();
  return field === "terminal" ? text.toUpperCase() : text;
}

function getRequestChangeFieldMeta(field) {
  return REQUEST_CHANGE_FIELD_META[field] || {};
}

function buildRequestChangeEntry(change, index) {
  const field = change?.field || "";
  const fieldMeta = getRequestChangeFieldMeta(field);
  const scope = change?.scope || fieldMeta.scope || "broker";
  const position = change?.position || null;
  const sensitive =
    Boolean(change?.sensitive) || Boolean(fieldMeta.sensitive) || field === "password";
  const scopeLabel = scope === "set" ? "Set" : `Broker ${position || "?"}`;
  const fieldLabel = change?.label || fieldMeta.label || field || "Field";

  return {
    key: `${scope}-${position || "set"}-${field || "field"}-${index}`,
    scopeLabel,
    fieldLabel,
    oldDisplay: sensitive ? "Changed" : getBrokerDisplayValue(change?.oldValue),
    newDisplay: sensitive ? "Changed" : getBrokerDisplayValue(change?.newValue),
    oldSearch: sensitive ? "" : String(change?.oldValue ?? ""),
    newSearch: sensitive ? "" : String(change?.newValue ?? ""),
  };
}

function getRequestPreviousSnapshot(request) {
  const details = request?.details || {};
  const previous = details.previous || {};
  const hasPreviousName = Object.prototype.hasOwnProperty.call(previous, "name");
  const previousBrokers = Array.isArray(previous.brokers) ? previous.brokers : null;
  const accountSetBrokers = Array.isArray(request?.AccountSet?.brokers)
    ? request.AccountSet.brokers
    : [];

  return {
    name: hasPreviousName ? previous.name : request?.AccountSet?.name,
    brokers: sortRequestBrokers(previousBrokers ?? accountSetBrokers),
  };
}

function getRequestNextSnapshot(request) {
  const details = request?.details || {};
  const hasRequestedName = Object.prototype.hasOwnProperty.call(details, "name");
  const requestedBrokers = Array.isArray(details.brokers) ? details.brokers : null;
  const fallbackBrokers = Array.isArray(request?.AccountSet?.brokers)
    ? request.AccountSet.brokers
    : [];

  return {
    name: hasRequestedName ? details.name : request?.AccountSet?.name,
    brokers: sortRequestBrokers(requestedBrokers ?? fallbackBrokers),
  };
}

function buildFallbackRequestChanges(request) {
  const previous = getRequestPreviousSnapshot(request);
  const next = getRequestNextSnapshot(request);
  const changes = [];

  if (
    normalizeRequestChangeComparableValue("name", previous.name) !==
    normalizeRequestChangeComparableValue("name", next.name)
  ) {
    changes.push(
      buildRequestChangeEntry(
        {
          scope: "set",
          field: "name",
          oldValue: previous.name,
          newValue: next.name,
        },
        changes.length,
      ),
    );
  }

  const previousByPosition = new Map(
    previous.brokers.map((broker, index) => [broker?.position || index + 1, broker]),
  );
  const nextByPosition = new Map(
    next.brokers.map((broker, index) => [broker?.position || index + 1, broker]),
  );
  const positions = Array.from(
    new Set([...previousByPosition.keys(), ...nextByPosition.keys()]),
  ).sort((a, b) => a - b);

  positions.forEach((position) => {
    const previousBroker = previousByPosition.get(position) || {};
    const nextBroker = nextByPosition.get(position) || {};

    REQUEST_CHANGE_BROKER_FIELDS.forEach((field) => {
      if (
        normalizeRequestChangeComparableValue(field, previousBroker?.[field]) ===
        normalizeRequestChangeComparableValue(field, nextBroker?.[field])
      ) {
        return;
      }

      changes.push(
        buildRequestChangeEntry(
          {
            scope: "broker",
            position,
            field,
            oldValue: previousBroker?.[field],
            newValue: nextBroker?.[field],
          },
          changes.length,
        ),
      );
    });
  });

  return changes;
}

function getRequestChanges(request) {
  if (request?.type !== "modify") return [];

  const details = request?.details || {};
  if (Object.prototype.hasOwnProperty.call(details, "changes")) {
    return Array.isArray(details.changes)
      ? details.changes.map((change, index) => buildRequestChangeEntry(change, index))
      : [];
  }

  return buildFallbackRequestChanges(request);
}

function getRequestChangesSearchText(request) {
  return getRequestChanges(request)
    .flatMap((change) => [change.scopeLabel, change.fieldLabel, change.oldSearch, change.newSearch])
    .join(" ")
    .toLowerCase();
}

export default function AdminAccounts() {
  const toast = useToast();
  const [notifications, setNotifications] = useState([]);
  const [notificationsUnread, setNotificationsUnread] = useState(0);
  const [notificationTotal, setNotificationTotal] = useState(0);
  const [notificationLoading, setNotificationLoading] = useState(true);
  const [notificationSearch, setNotificationSearch] = useState("");
  const [notificationSorting, setNotificationSorting] = useState([]);
  const [notificationPagination, setNotificationPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [requests, setRequests] = useState([]);
  const [accountSets, setAccountSets] = useState([]);
  const [deletedAccountSets, setDeletedAccountSets] = useState([]);
  const [requestFilter, setRequestFilter] = useState("pending");
  const [requestSearch, setRequestSearch] = useState("");
  const [requestSorting, setRequestSorting] = useState([]);
  const [requestPagination, setRequestPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [accountSetFilter, setAccountSetFilter] = useState("all");
  const [accountSetSearch, setAccountSetSearch] = useState("");
  const [accountSetSorting, setAccountSetSorting] = useState([]);
  const [accountSetPagination, setAccountSetPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [actionBusy, setActionBusy] = useState(null);
  const [setFileBusy, setSetFileBusy] = useState(null);
  const [requestSetFileBusy, setRequestSetFileBusy] = useState(null);
  const [deleteAccountSetId, setDeleteAccountSetId] = useState(null);
  const [deleteNotificationTarget, setDeleteNotificationTarget] = useState(null);
  const [setConfigGuideOpen, setSetConfigGuideOpen] = useState(false);
  const [focusedRequestId, setFocusedRequestId] = useState(null);
  const [visibleBrokerPasswords, setVisibleBrokerPasswords] = useState({});
  const [requestConfigModal, setRequestConfigModal] = useState({
    open: false,
    source: "request",
    request: null,
    accountSet: {},
    files: [],
    warning: null,
    duplicateLoginDetected: false,
  });
  const reviewSectionRef = useRef(null);
  const requestRowRefs = useRef(new Map());

  const loadNotifications = useCallback(
    async ({
      pageIndex = notificationPagination.pageIndex,
      pageSize = notificationPagination.pageSize,
      silent = false,
    } = {}) => {
      if (!silent) {
        setNotificationLoading(true);
      }

      try {
        const offset = pageIndex * pageSize;
        const res = await API.get("/admin/notifications", {
          params: {
            limit: pageSize,
            offset,
          },
        });
        const payload = res.data || {};
        const items = Array.isArray(payload.items)
          ? payload.items
          : Array.isArray(payload.data)
            ? payload.data
            : [];

        setNotifications(items);
        setNotificationsUnread(payload.unreadCount || 0);
        setNotificationTotal(Number(payload.total) || Number(payload.count) || items.length);
      } catch (err) {
        if (!silent) {
          toast.error(err.response?.data?.error || err.message || "Failed to load notifications.");
        }
        setNotifications([]);
        setNotificationsUnread(0);
        setNotificationTotal(0);
      } finally {
        setNotificationLoading(false);
      }
    },
    [notificationPagination.pageIndex, notificationPagination.pageSize, toast],
  );

  const loadAccounts = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const next = await loadAdminAccountsData();
      setRequests(next.requests);
      setAccountSets(next.accountSets);
      setDeletedAccountSets(next.deletedAccountSets);
      setError(
        next.errors.length
          ? `Some account data could not be loaded: ${next.errors.join(", ")}.`
          : "",
      );
      return next;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!setConfigGuideOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;
    const privateShell = document.querySelector(".app-shell-private");
    const previousPrivateShellOverflow = privateShell?.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    if (privateShell) {
      privateShell.style.overflow = "hidden";
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSetConfigGuideOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
      if (privateShell) {
        privateShell.style.overflow = previousPrivateShellOverflow || "";
      }
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [setConfigGuideOpen]);

  useEffect(() => {
    if (!requestConfigModal.open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setRequestConfigModal({
          open: false,
          source: "request",
          request: null,
          accountSet: {},
          files: [],
          warning: null,
          duplicateLoginDetected: false,
        });
        setRequestSetFileBusy(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [requestConfigModal.open]);

  useEffect(() => {
    if (!focusedRequestId) return undefined;

    const timer = window.setTimeout(() => {
      const row = requestRowRefs.current.get(String(focusedRequestId));
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      reviewSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);

    const clearTimer = window.setTimeout(() => {
      setFocusedRequestId(null);
    }, 3200);

    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clearTimer);
    };
  }, [focusedRequestId, requestPagination.pageIndex, requestPagination.pageSize]);

  const filteredRequests = useMemo(() => {
    const term = requestSearch.trim().toLowerCase();

    return requests.filter((request) => {
      const matchesStatus = requestFilter === "all" || request.status === requestFilter;
      const details = request.details || {};
      const requestTypeMeta = getAccountSetRequestTypeMeta(request.type);
      const sourceBrokers = getRequestSnapshotBrokers(request);
      const changesSearchText = getRequestChangesSearchText(request);
      const brokerText = Array.isArray(sourceBrokers)
        ? sourceBrokers
            .map(
              (broker) =>
                `${broker.terminal} ${broker.brokerName} ${broker.server} ${broker.accountNumber} ${broker.requestedSymbol}`,
            )
            .join(" ")
        : "";
      const matchesSearch =
        !term ||
        [
          request.User?.email,
          request.AccountSet?.name,
          request.type,
          requestTypeMeta.label,
          request.reason,
          details.name,
          brokerText,
          changesSearchText,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(term),
        );

      return matchesStatus && matchesSearch;
    });
  }, [requests, requestFilter, requestSearch]);

  const filteredNotifications = useMemo(() => {
    const term = notificationSearch.trim().toLowerCase();
    if (!term) return notifications;

    return notifications.filter((notification) => {
      const payload = notification.payload || {};
      const accountSetName = payload.accountSet?.name || notification.accountSet?.name || "";
      const accountSetId = payload.accountSet?.id || notification.accountSetId || "";
      const requestId = payload.requestId || "";
      const userEmail = payload.user?.email || notification.user?.email || "";
      return [
        accountSetName,
        accountSetId,
        requestId,
        userEmail,
        notification.status,
        payload.kind,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(term),
      );
    });
  }, [notificationSearch, notifications]);

  const accountSetRowsByFilter = useMemo(() => {
    const currentRows = accountSets.map((accountSet) => buildAccountSetRow(accountSet));
    const deletedRows = deletedAccountSets.map((accountSet) =>
      buildAccountSetRow(accountSet, "deleted"),
    );
    const allRowsById = new Map();

    currentRows.forEach((row) => {
      allRowsById.set(String(row.setId), row);
    });

    deletedRows.forEach((row) => {
      allRowsById.set(String(row.setId), row);
    });

    return {
      all: Array.from(allRowsById.values()),
      active: currentRows.filter((row) => row.rowStatus === "active"),
      reconnect_required: currentRows.filter((row) => row.rowStatus === "reconnect_required"),
      pending_review: currentRows.filter((row) => row.rowStatus === "pending_review"),
      draft: currentRows.filter((row) => row.rowStatus === "draft"),
      deleted: deletedRows,
    };
  }, [accountSets, deletedAccountSets]);

  const visibleAccountSetRows = useMemo(
    () => accountSetRowsByFilter[accountSetFilter] || accountSetRowsByFilter.all,
    [accountSetFilter, accountSetRowsByFilter],
  );

  const filteredAccountSetRows = useMemo(() => {
    const term = accountSetSearch.trim().toLowerCase();
    if (!term) return visibleAccountSetRows;

    return visibleAccountSetRows.filter((accountSetRow) => accountSetRow.searchText.includes(term));
  }, [accountSetSearch, visibleAccountSetRows]);

  useEffect(() => {
    setAccountSetPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [accountSetFilter, accountSetSearch]);

  useEffect(() => {
    setRequestPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [requestFilter, requestSearch]);

  useEffect(() => {
    setNotificationPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [notificationSearch]);

  const handleRequestAction = useCallback(
    async (requestId, action) => {
      setActionBusy({ type: "request", id: requestId });
      try {
        await API.post(`/admin/account-set-requests/${requestId}/${action}`);
        toast.success(action === "approve" ? "Request approved." : "Request rejected.");
        await loadAccounts(true);
      } catch (err) {
        toast.error(err.response?.data?.msg || err.message);
      } finally {
        setActionBusy(null);
      }
    },
    [loadAccounts, toast],
  );

  const handleMarkAllNotificationsRead = useCallback(async () => {
    setActionBusy({ type: "mark-all" });
    try {
      await API.post("/admin/notifications/mark-all-read");
      toast.success("All notifications marked as read.");
      await loadAccounts(true);
      await loadNotifications({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setActionBusy(null);
    }
  }, [loadAccounts, loadNotifications, toast]);

  const handleMarkNotificationRead = useCallback(
    async (id) => {
      setActionBusy({ type: "notification", id });
      try {
        await API.post("/admin/notifications/mark-read", { ids: [id] });
        toast.success("Notification marked as read.");
        await loadNotifications({ silent: true });
      } catch (err) {
        toast.error(err.response?.data?.error || err.message);
      } finally {
        setActionBusy(null);
      }
    },
    [loadNotifications, toast],
  );

  const handleDeleteNotification = useCallback(async () => {
    if (!deleteNotificationTarget?.id) return;

    setActionBusy({ type: "delete-notification", id: deleteNotificationTarget.id });
    try {
      const res = await API.delete(`/admin/notifications/${deleteNotificationTarget.id}`);
      const payload = res.data || {};

      if (typeof payload.unreadCount === "number") {
        setNotificationsUnread(payload.unreadCount);
      }
      if (payload.deleted) {
        setNotificationTotal((prev) => Math.max(0, prev - 1));
      }

      toast.success("Notification deleted.");
      setDeleteNotificationTarget(null);

      if (notifications.length === 1 && notificationPagination.pageIndex > 0) {
        setNotificationPagination((prev) => ({
          ...prev,
          pageIndex: Math.max(0, prev.pageIndex - 1),
        }));
      } else {
        await loadNotifications({ silent: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to delete notification.");
    } finally {
      setActionBusy(null);
    }
  }, [
    deleteNotificationTarget,
    loadNotifications,
    notificationPagination.pageIndex,
    notifications.length,
    toast,
  ]);

  const handleOpenReviewRequest = useCallback(
    async (requestId, { refresh = false } = {}) => {
      if (!requestId) {
        toast.error("Missing request ID.");
        return;
      }

      if (refresh) {
        setActionBusy({ type: "open-request", id: requestId });
      }

      try {
        let nextRequests = requests;
        if (refresh || !requests.some((request) => String(request.id) === String(requestId))) {
          const next = await loadAccounts(true);
          nextRequests = Array.isArray(next?.requests) ? next.requests : [];
        }

        setRequestFilter("all");
        setRequestSearch("");
        setRequestSorting([]);

        const nextIndex = nextRequests.findIndex(
          (request) => String(request.id) === String(requestId),
        );
        if (nextIndex < 0) {
          toast.error("Request not found in review queue.");
          return;
        }

        const pageIndex = Math.floor(nextIndex / Math.max(1, requestPagination.pageSize));
        window.setTimeout(() => {
          setRequestPagination((prev) => ({
            ...prev,
            pageIndex,
          }));
          setFocusedRequestId(String(requestId));
          reviewSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
      } finally {
        if (refresh) {
          setActionBusy(null);
        }
      }
    },
    [loadAccounts, requestPagination.pageSize, requests, toast],
  );

  const fetchRequestCollectorSetFiles = useCallback(async (requestId) => {
    const encodedRequestId = encodeURIComponent(String(requestId));
    const res = await API.get(
      `/admin/account-set-requests/${encodedRequestId}/collector-set-files`,
    );
    return normalizeCollectorSetFilesResponse(res.data || {});
  }, []);

  const handleOpenRequestedConfig = useCallback(
    async (request) => {
      const requestId = request?.id;
      if (!requestId) {
        toast.error("Missing request ID for requested config.");
        return;
      }

      setActionBusy({ type: "request-config", id: requestId });
      try {
        const payload = await fetchRequestCollectorSetFiles(requestId);
        setRequestConfigModal({
          open: true,
          source: "request",
          request,
          accountSet: payload.accountSet || {},
          files: payload.files || [],
          warning: payload.warning || null,
          duplicateLoginDetected: Boolean(payload.duplicateLoginDetected),
        });
      } catch (err) {
        toast.error(err.response?.data?.error || err.message || "Failed to load requested config.");
      } finally {
        setActionBusy(null);
      }
    },
    [fetchRequestCollectorSetFiles, toast],
  );

  const handleCloseRequestedConfig = useCallback(() => {
    setRequestConfigModal({
      open: false,
      source: "request",
      request: null,
      accountSet: {},
      files: [],
      warning: null,
      duplicateLoginDetected: false,
    });
    setRequestSetFileBusy(null);
  }, []);

  const handlePermanentDelete = useCallback(async () => {
    if (!deleteAccountSetId) return;
    setActionBusy({ type: "permanent-delete", id: deleteAccountSetId });
    try {
      await API.delete(`/admin/account-sets/${deleteAccountSetId}/permanent`);
      toast.success("Account set permanently deleted.");
      setDeleteAccountSetId(null);
      await loadAccounts(true);
    } catch (err) {
      toast.error(err.response?.data?.msg || err.message);
    } finally {
      setActionBusy(null);
    }
  }, [deleteAccountSetId, loadAccounts, toast]);

  const handleCopyValue = useCallback(
    async (value, successLabel = "Copied") => {
      if (!value) return;

      if (!navigator?.clipboard?.writeText) {
        toast.error("Clipboard API not available in this browser context.");
        return;
      }

      try {
        await navigator.clipboard.writeText(String(value));
        toast.info(`${successLabel} copied to clipboard.`);
      } catch (err) {
        toast.error(`Failed to copy ${successLabel}.`);
      }
    },
    [toast],
  );

  const downloadTextFile = useCallback((filename, content) => {
    if (!filename || !content) {
      throw new Error("Missing file content for download.");
    }

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, []);

  const handleCopyRequestedCollectorSetFile = useCallback(
    async (file) => {
      if (!navigator?.clipboard?.writeText) {
        toast.error("Clipboard API not available in this browser context.");
        return;
      }

      const modalConfigId =
        requestConfigModal.request?.id ||
        requestConfigModal.accountSet?.id ||
        requestConfigModal.accountSet?._id ||
        requestConfigModal.accountSet?.setId;
      if (!modalConfigId) {
        toast.error("Missing request ID for set file copy.");
        return;
      }

      const brokerPosition = file?.brokerPosition || file?.position || "";
      setRequestSetFileBusy({ id: modalConfigId, action: `copy-${brokerPosition}` });
      try {
        const clipboardText = buildCollectorSetSingleFileClipboardText({
          requestId: requestConfigModal.request?.id || null,
          requestType: requestConfigModal.request?.type || null,
          accountSet: requestConfigModal.accountSet || {},
          duplicateLoginDetected: Boolean(requestConfigModal.duplicateLoginDetected),
          warning: requestConfigModal.warning || null,
          file,
        });
        if (!clipboardText) {
          throw new Error("Failed to build requested set file clipboard text.");
        }

        await navigator.clipboard.writeText(clipboardText);
        toast.info(
          `${file?.brokerName || file?.terminal || "Requested"} set file copied to clipboard.`,
        );
      } catch (err) {
        toast.error(
          err.response?.data?.error || err.message || "Failed to copy requested set file.",
        );
      } finally {
        setRequestSetFileBusy(null);
      }
    },
    [requestConfigModal, toast],
  );

  const handleDownloadRequestedCollectorSetFile = useCallback(
    async (file) => {
      const modalConfigId =
        requestConfigModal.request?.id ||
        requestConfigModal.accountSet?.id ||
        requestConfigModal.accountSet?._id ||
        requestConfigModal.accountSet?.setId;
      if (!modalConfigId) {
        toast.error("Missing request ID for set file download.");
        return;
      }

      const brokerPosition = file?.brokerPosition || file?.position || "";
      setRequestSetFileBusy({ id: modalConfigId, action: `download-${brokerPosition}` });
      try {
        const filename = getCollectorSetDownloadFilename(file);
        downloadTextFile(filename, file?.content || "");
        toast.success(`${filename} downloaded.`);
      } catch (err) {
        toast.error(
          err.response?.data?.error || err.message || "Failed to download requested set file.",
        );
      } finally {
        setRequestSetFileBusy(null);
      }
    },
    [downloadTextFile, requestConfigModal, toast],
  );

  const fetchCollectorSetFile = useCallback(async (accountSetId, brokerId) => {
    const encodedAccountSetId = encodeURIComponent(String(accountSetId));
    const encodedBrokerId = encodeURIComponent(String(brokerId));
    const res = await API.get(
      `/admin/account-sets/${encodedAccountSetId}/collector-set-file/${encodedBrokerId}`,
    );
    return normalizeCollectorSetFileResponse(res.data || {});
  }, []);

  const handleOpenSetConfig = useCallback(
    async (accountSet) => {
      const accountSetId = accountSet?.setId;
      if (!accountSetId) {
        toast.error("Missing account set ID for set config.");
        return;
      }

      const collectorSetBrokers = accountSet?.collectorSetBrokers || [];
      if (collectorSetBrokers.length === 0) {
        toast.error("No brokers found for set config.");
        return;
      }

      setSetFileBusy({ id: accountSetId, action: "open-config" });
      try {
        const payloads = await Promise.all(
          collectorSetBrokers.map((broker) => fetchCollectorSetFile(accountSetId, broker.brokerId)),
        );
        const warnings = Array.from(
          new Set(payloads.map((payload) => payload.warning).filter(Boolean)),
        );
        const firstAccountSet = payloads.find((payload) => payload.accountSet)?.accountSet || {};

        setRequestConfigModal({
          open: true,
          source: "set",
          request: null,
          accountSet: {
            ...accountSet,
            ...firstAccountSet,
            id: firstAccountSet.id || firstAccountSet._id || accountSet.setId,
            name: firstAccountSet.name || accountSet.setName,
          },
          files: payloads.map((payload) => payload.file),
          warning: warnings.join(" | ") || null,
          duplicateLoginDetected: payloads.some((payload) => payload.duplicateLoginDetected),
        });
      } catch (err) {
        toast.error(err.response?.data?.error || err.message || "Failed to load set config.");
      } finally {
        setSetFileBusy(null);
      }
    },
    [fetchCollectorSetFile, toast],
  );

  const handleOpenPermanentDeleteModal = useCallback((setId) => {
    setDeleteAccountSetId(setId);
  }, []);

  const toggleBrokerPasswordVisibility = useCallback((visibilityKey) => {
    setVisibleBrokerPasswords((prev) => ({
      ...prev,
      [visibilityKey]: !prev[visibilityKey],
    }));
  }, []);

  const renderBrokerInfoLine = useCallback(
    (broker, { scope = "broker", rowId = "row", index = 0, duplicate = false } = {}) => {
      const brokerKey =
        broker?.key ||
        broker?.id ||
        broker?._id ||
        broker?.position ||
        broker?.accountNumber ||
        index;
      const password = String(broker?.password ?? "").trim();
      const visibilityKey = `${scope}:${rowId}:${brokerKey}`;
      const passwordVisible = Boolean(visibleBrokerPasswords[visibilityKey]);
      const passwordLabel = passwordVisible ? password : maskBrokerPassword(password);
      const brokerName = getBrokerDisplayValue(broker?.brokerName || broker?.name);
      const terminal = getBrokerDisplayValue(broker?.terminal);
      const server = getBrokerDisplayValue(broker?.server);
      const accountNumber = getBrokerDisplayValue(broker?.accountNumber);

      return (
        <div
          key={`${rowId}-${brokerKey}`}
          className={`admin-accounts-broker-line${duplicate ? " admin-accounts-broker-line-duplicate" : ""}`}
        >
          <span className="admin-accounts-broker-top">
            {terminal}
            {broker?.requestedSymbol ? ` (${broker.requestedSymbol})` : ""}
          </span>
          <span className="admin-accounts-broker-bottom">{brokerName}</span>
          <span className="admin-accounts-broker-account">{server}</span>
          <span className="admin-accounts-broker-account">{accountNumber}</span>
          <span className="admin-accounts-broker-password">
            <span className="admin-accounts-broker-password-value">{passwordLabel}</span>
            {password && (
              <button
                type="button"
                className="admin-accounts-password-toggle"
                onClick={() => toggleBrokerPasswordVisibility(visibilityKey)}
                aria-label={passwordVisible ? "Hide password" : "Show password"}
                title={passwordVisible ? "Hide password" : "Show password"}
              >
                {passwordVisible ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            )}
          </span>
        </div>
      );
    },
    [toggleBrokerPasswordVisibility, visibleBrokerPasswords],
  );

  const accountSetColumns = useMemo(
    () => [
      {
        id: "serial",
        header: "S. No.",
        cell: ({ row, table }) =>
          row.index +
          1 +
          table.getState().pagination.pageIndex * table.getState().pagination.pageSize,
        enableSorting: false,
      },
      {
        accessorKey: "rowStatus",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={`admin-badge ${ACCOUNT_SET_STATUS_META[row.original.rowStatus]?.badgeClass || "admin-badge-neutral"}`}
          >
            {ACCOUNT_SET_STATUS_META[row.original.rowStatus]?.label ||
              row.original.rowStatus ||
              "Unknown"}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.userEmail,
        id: "user",
        header: "User",
        cell: ({ row }) => row.original.userEmail,
      },
      {
        accessorKey: "setName",
        header: "Set",
        cell: ({ row }) => (
          <div className="admin-accounts-request-set">
            <span className="admin-accounts-request-set-name">{row.original.setName}</span>
            <button
              type="button"
              className="admin-accounts-set-id-button"
              onClick={() => handleCopyValue(row.original.setId, "Set ID")}
              title="Copy Set ID"
            >
              {row.original.setId || "—"}
            </button>
          </div>
        ),
      },
      {
        accessorFn: (row) => row.brokerIds.map((broker) => broker.value).join(" "),
        id: "brokerIds",
        header: "Broker ID",
        cell: ({ row }) => {
          return (
            <div className="admin-accounts-id-stack">
              {row.original.brokerIds.map((broker) => (
                <div key={broker.key} className="admin-accounts-id-item">
                  <span className="admin-accounts-id-label">{broker.label}</span>
                  <button
                    type="button"
                    className="admin-accounts-set-id-button"
                    onClick={() =>
                      handleCopyValue(broker.value === "—" ? "" : broker.value, broker.copyLabel)
                    }
                    title="Copy Broker ID"
                  >
                    {broker.value}
                  </button>
                </div>
              ))}
            </div>
          );
        },
      },
      {
        id: "brokers",
        header: "Broker Info",
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="admin-accounts-brokers-cell">
              {row.original.brokerInfoRows.length > 0 ? (
                row.original.brokerInfoRows.map((broker, index) =>
                  renderBrokerInfoLine(broker, {
                    scope: "set-management",
                    rowId: row.original.setId,
                    index,
                  }),
                )
              ) : (
                <span className="admin-accounts-no-action">No brokers</span>
              )}
            </div>
          );
        },
      },
      {
        id: "action",
        header: "Config",
        enableSorting: false,
        legacyCell: ({ row }) =>
          row.original.rowStatus === "deleted" ? (
            <button
              className="admin-btn admin-btn-danger admin-accounts-delete-btn"
              onClick={() => handleOpenPermanentDeleteModal(row.original.setId)}
              disabled={
                actionBusy?.type === "permanent-delete" && actionBusy?.id === row.original.setId
              }
            >
              <Trash2 size={15} />
              Permanently Delete
            </button>
          ) : (
            <span className="admin-accounts-no-action">—</span>
          ),
        cell: ({ row }) => {
          const busyForRow = setFileBusy?.id === row.original.setId;
          const collectorSetBrokers = row.original.collectorSetBrokers || [];

          if (row.original.rowStatus === "deleted") {
            return (
              <button
                className="admin-btn admin-btn-danger admin-accounts-delete-btn"
                onClick={() => handleOpenPermanentDeleteModal(row.original.setId)}
                disabled={
                  actionBusy?.type === "permanent-delete" && actionBusy?.id === row.original.setId
                }
              >
                <Trash2 size={15} />
                Permanently Delete
              </button>
            );
          }

          return (
            <div className="admin-accounts-set-actions">
              {collectorSetBrokers.length === 0 ? (
                <span className="admin-accounts-no-action">No brokers</span>
              ) : (
                <button
                  type="button"
                  className="admin-btn admin-btn-primary admin-accounts-set-action-btn"
                  onClick={() => handleOpenSetConfig(row.original)}
                  disabled={busyForRow}
                  aria-label={`Open ${row.original.setName || "account set"} config`}
                  title="Open set config"
                >
                  <Eye size={15} />
                  <span className="admin-accounts-set-action-label">
                    {busyForRow && setFileBusy?.action === "open-config" ? "Loading..." : "Config"}
                  </span>
                </button>
              )}
            </div>
          );
        },
      },
      {
        accessorFn: (row) => row.createdLabel,
        header: "Created",
        cell: ({ row }) => row.original.createdLabel,
      },
      {
        accessorFn: (row) => row.deletedLabel,
        header: "Deleted",
        cell: ({ row }) => row.original.deletedLabel,
      },
    ],
    [
      actionBusy,
      handleCopyValue,
      handleOpenPermanentDeleteModal,
      handleOpenSetConfig,
      renderBrokerInfoLine,
      setFileBusy,
    ],
  );

  const accountSetTable = useReactTable({
    data: filteredAccountSetRows,
    columns: accountSetColumns,
    state: {
      sorting: accountSetSorting,
      pagination: accountSetPagination,
    },
    onSortingChange: setAccountSetSorting,
    onPaginationChange: setAccountSetPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortDescFirst: true,
  });

  const accountSetRows = accountSetTable.getRowModel().rows;
  const accountSetRowStart =
    filteredAccountSetRows.length === 0
      ? 0
      : accountSetPagination.pageIndex * accountSetPagination.pageSize + 1;
  const accountSetRowEnd = Math.min(
    (accountSetPagination.pageIndex + 1) * accountSetPagination.pageSize,
    filteredAccountSetRows.length,
  );

  const notificationColumns = useMemo(
    () => [
      {
        id: "serial",
        header: "S. No.",
        cell: ({ row }) =>
          row.index + 1 + notificationPagination.pageIndex * notificationPagination.pageSize,
        enableSorting: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={`admin-badge ${row.original.status === "unread" ? "admin-badge-info" : "admin-badge-neutral"}`}
          >
            {row.original.status === "unread" ? "Unread" : "Read"}
          </span>
        ),
      },
      {
        id: "user",
        header: "User",
        accessorFn: (row) => row.payload?.user?.email || row.user?.email || "Unknown",
        cell: ({ row }) =>
          row.original.payload?.user?.email || row.original.user?.email || "Unknown",
      },
      {
        id: "set",
        header: "Set",
        accessorFn: (row) => row.payload?.accountSet?.name || row.accountSet?.name || "Unknown",
        cell: ({ row }) => {
          const payload = row.original.payload || {};
          const name = payload.accountSet?.name || row.original.accountSet?.name || "Unknown";
          const setId = payload.accountSet?.id || row.original.accountSetId || "Unknown";
          return (
            <div className="admin-accounts-request-set">
              <span className="admin-accounts-request-set-name">{name}</span>
              <button
                type="button"
                className="admin-accounts-set-id-button"
                onClick={() => handleCopyValue(setId, "Set ID")}
                title="Copy Set ID"
              >
                {setId}
              </button>
            </div>
          );
        },
      },
      {
        id: "linked",
        header: "Linked",
        accessorFn: (row) => row.createdAt,
        cell: ({ row }) => formatTimestamp(row.original.createdAt),
      },
      {
        id: "brokerInfo",
        header: "Broker Info",
        enableSorting: false,
        cell: ({ row }) => {
          const payload = row.original.payload || {};
          const brokers = Array.isArray(payload.brokers) ? payload.brokers : [];
          const accountSetBrokers = Array.isArray(row.original.accountSet?.brokers)
            ? row.original.accountSet.brokers
            : [];
          const dupById = new Map(accountSetBrokers.map((broker) => [String(broker.id), broker]));
          const dupByPos = new Map(
            accountSetBrokers.map((broker) => [String(broker.position), broker]),
          );

          return (
            <div className="admin-accounts-brokers-cell">
              {brokers.length > 0 ? (
                brokers.map((broker, index) => {
                  const duplicateSource =
                    dupById.get(String(broker.id)) || dupByPos.get(String(broker.position));
                  const brokerDuplicate = Boolean(duplicateSource?.duplicateLogin);
                  return renderBrokerInfoLine(broker, {
                    scope: "notification",
                    rowId: row.original.id,
                    index,
                    duplicate: brokerDuplicate,
                  });
                })
              ) : (
                <span className="admin-accounts-no-action">No brokers</span>
              )}
            </div>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        enableSorting: false,
        cell: ({ row }) => {
          const accountSetBrokers = Array.isArray(row.original.accountSet?.brokers)
            ? row.original.accountSet.brokers
            : [];
          const duplicateLoginDetected =
            Boolean(row.original.duplicateLoginDetected) ||
            accountSetBrokers.some((broker) => broker.duplicateLogin);
          const isUnread = row.original.status === "unread";
          const requestId = row.original.payload?.requestId || null;

          return (
            <div className="admin-accounts-notification-actions">
              {duplicateLoginDetected && (
                <span className="admin-accounts-notification-flag">Duplicate Login</span>
              )}
              <div className="admin-accounts-notification-primary-actions">
                <button
                  className="admin-btn admin-btn-ghost"
                  onClick={() => handleOpenReviewRequest(requestId, { refresh: true })}
                  disabled={
                    !requestId ||
                    (actionBusy?.type === "open-request" && actionBusy?.id === requestId)
                  }
                >
                  <ChevronRight size={15} />
                  Open
                </button>
                <button
                  className="admin-btn admin-btn-ghost"
                  onClick={() => handleMarkNotificationRead(row.original.id)}
                  disabled={
                    !isUnread ||
                    (actionBusy?.type === "notification" && actionBusy?.id === row.original.id)
                  }
                >
                  <Check size={15} />
                  Read
                </button>
              </div>
              <button
                className="admin-btn admin-btn-danger admin-accounts-delete-btn"
                onClick={() => setDeleteNotificationTarget(row.original)}
                disabled={
                  actionBusy?.type === "delete-notification" && actionBusy?.id === row.original.id
                }
              >
                <Trash2 size={15} />
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [
      actionBusy,
      handleCopyValue,
      handleMarkNotificationRead,
      handleOpenReviewRequest,
      notificationPagination.pageIndex,
      notificationPagination.pageSize,
      renderBrokerInfoLine,
    ],
  );

  const notificationTable = useReactTable({
    data: filteredNotifications,
    columns: notificationColumns,
    state: {
      sorting: notificationSorting,
    },
    onSortingChange: setNotificationSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: true,
  });

  const notificationRows = notificationTable.getRowModel().rows;
  const skeletonRows = Array.from({ length: 6 });
  const notificationTotalPages = Math.max(
    1,
    Math.ceil(notificationTotal / notificationPagination.pageSize),
  );
  const notificationRowStart =
    notificationTotal === 0
      ? 0
      : notificationPagination.pageIndex * notificationPagination.pageSize + 1;
  const notificationRowEnd = Math.min(
    (notificationPagination.pageIndex + 1) * notificationPagination.pageSize,
    notificationTotal,
  );

  const requestColumns = useMemo(
    () => [
      {
        id: "serial",
        header: "S. No.",
        cell: ({ row, table }) =>
          row.index +
          1 +
          table.getState().pagination.pageIndex * table.getState().pagination.pageSize,
        enableSorting: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={`admin-badge ${
              row.original.status === "approved"
                ? "admin-badge-success"
                : row.original.status === "rejected"
                  ? "admin-badge-danger"
                  : "admin-badge-warning"
            }`}
          >
            {row.original.status}
          </span>
        ),
      },
      {
        accessorFn: (row) => getAccountSetRequestTypeMeta(row.type).label,
        id: "type",
        header: "Request",
        cell: ({ row }) => {
          const requestTypeMeta = getAccountSetRequestTypeMeta(row.original.type);
          return (
            <span className={`admin-badge ${requestTypeMeta.badgeClass}`}>
              {requestTypeMeta.shortLabel}
            </span>
          );
        },
      },
      {
        accessorFn: (row) => row.User?.email || "Unknown",
        id: "user",
        header: "User",
        cell: ({ row }) => row.original.User?.email || "Unknown",
      },
      {
        accessorFn: (row) => row.details?.name || row.AccountSet?.name || "Unknown",
        id: "set",
        header: "Set",
        cell: ({ row }) => (
          <div className="admin-accounts-request-set">
            <span className="admin-accounts-request-set-name">
              {row.original.details?.name || row.original.AccountSet?.name || "Unknown"}
            </span>
            <button
              type="button"
              className="admin-accounts-set-id-button"
              onClick={() =>
                handleCopyValue(row.original.accountSetId || row.original.AccountSet?.id, "Set ID")
              }
              title="Copy Set ID"
            >
              {row.original.accountSetId || row.original.AccountSet?.id || "—"}
            </button>
          </div>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Submitted",
        cell: ({ row }) => formatTimestamp(row.original.createdAt),
      },
      {
        id: "reason",
        header: "Reason",
        enableSorting: false,
        cell: ({ row }) => {
          return <div className="admin-accounts-request-reason">{row.original.reason || "—"}</div>;
        },
      },
      {
        id: "changes",
        header: "Changes",
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.type !== "modify") {
            return <span className="admin-accounts-no-action">-</span>;
          }

          const changes = getRequestChanges(row.original);
          if (changes.length === 0) {
            return <span className="admin-accounts-request-change-empty">No field changes</span>;
          }

          return (
            <div className="admin-accounts-request-changes">
              {changes.map((change) => (
                <div key={change.key} className="admin-accounts-request-change-item">
                  <span className="admin-accounts-request-change-label">
                    {change.scopeLabel} / {change.fieldLabel}
                  </span>
                  <div className="admin-accounts-request-change-values">
                    <span className="admin-accounts-request-change-old">{change.oldDisplay}</span>
                    <span className="admin-accounts-request-change-arrow" aria-hidden="true">
                      →
                    </span>
                    <span className="admin-accounts-request-change-new">{change.newDisplay}</span>
                  </div>
                </div>
              ))}
            </div>
          );
        },
      },
      {
        id: "brokerInfo",
        header: "Broker Info",
        enableSorting: false,
        cell: ({ row }) => {
          const brokers = getRequestSnapshotBrokers(row.original);
          return (
            <div className="admin-accounts-brokers-cell">
              {brokers.length > 0 ? (
                brokers.map((broker, index) =>
                  renderBrokerInfoLine(broker, {
                    scope: "review-request",
                    rowId: row.original.id,
                    index,
                  }),
                )
              ) : (
                <span className="admin-accounts-no-action">—</span>
              )}
            </div>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        enableSorting: false,
        cell: ({ row }) => {
          const requestTypeMeta = getAccountSetRequestTypeMeta(row.original.type);
          const requestBusy = actionBusy?.type === "request" && actionBusy?.id === row.original.id;
          const previewBusy =
            actionBusy?.type === "request-config" && actionBusy?.id === row.original.id;
          const canPreview = row.original.status === "pending" && requestTypeMeta.canPreview;

          return row.original.status === "pending" ? (
            <div className="admin-table-actions admin-accounts-request-actions">
              {canPreview && (
                <button
                  type="button"
                  className="admin-btn admin-btn-primary admin-accounts-icon-btn"
                  onClick={() => handleOpenRequestedConfig(row.original)}
                  disabled={previewBusy}
                  aria-label="Requested config"
                  title={previewBusy ? "Loading requested config..." : "Requested Config"}
                >
                  <Eye size={15} />
                </button>
              )}
              <button
                className="admin-btn admin-btn-success admin-accounts-icon-btn"
                onClick={() => handleRequestAction(row.original.id, "approve")}
                disabled={requestBusy}
                aria-label="Approve request"
                title="Approve"
              >
                <Check size={15} />
              </button>
              <button
                className="admin-btn admin-btn-danger admin-accounts-icon-btn"
                onClick={() => handleRequestAction(row.original.id, "reject")}
                disabled={requestBusy}
                aria-label="Reject request"
                title="Reject"
              >
                <X size={15} />
              </button>
            </div>
          ) : (
            <span className="admin-accounts-no-action">Processed</span>
          );
        },
      },
    ],
    [
      actionBusy,
      handleCopyValue,
      handleOpenRequestedConfig,
      handleRequestAction,
      renderBrokerInfoLine,
    ],
  );

  const requestTable = useReactTable({
    data: filteredRequests,
    columns: requestColumns,
    state: {
      sorting: requestSorting,
      pagination: requestPagination,
    },
    onSortingChange: setRequestSorting,
    onPaginationChange: setRequestPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortDescFirst: true,
  });

  const requestRows = requestTable.getRowModel().rows;
  const requestRowStart =
    filteredRequests.length === 0
      ? 0
      : requestPagination.pageIndex * requestPagination.pageSize + 1;
  const requestRowEnd = Math.min(
    (requestPagination.pageIndex + 1) * requestPagination.pageSize,
    filteredRequests.length,
  );

  const renderHeaderSort = (header) => {
    const sorted = header.column.getIsSorted();
    return (
      <button
        type="button"
        className={`admin-accounts-sort${sorted ? " admin-accounts-sort-active" : ""}`}
        onClick={header.column.getToggleSortingHandler()}
      >
        <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
        {sorted ? (
          <ChevronDown
            className={`admin-accounts-sort-icon${sorted === "desc" ? " admin-accounts-sort-icon-desc" : ""}`}
          />
        ) : (
          <ChevronsUpDown className="admin-accounts-sort-icon" />
        )}
      </button>
    );
  };

  const renderSetConfigGuideModal = () => (
    <div
      className="admin-accounts-config-modal-overlay"
      onClick={() => setSetConfigGuideOpen(false)}
      role="presentation"
    >
      <div
        className="admin-accounts-config-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="collector-config-guide-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-accounts-config-modal-scroll">
          <div className="admin-accounts-config-modal-header">
            <div className="admin-accounts-config-modal-heading">
              <span className="admin-accounts-config-modal-icon">
                <Info size={18} />
              </span>
              <div>
                <h3 id="collector-config-guide-title">MT4/MT5 set-file guide</h3>
                <p>
                  Use this guide when reviewing generated collector configs before copying or
                  downloading them.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="admin-accounts-config-modal-close"
              onClick={() => setSetConfigGuideOpen(false)}
              aria-label="Close set-file guide"
            >
              <X size={16} />
            </button>
          </div>

          <div className="admin-accounts-config-callouts">
            {COLLECTOR_SET_GUIDE_CALLOUTS.map((callout) => (
              <div key={callout.title} className="admin-accounts-config-callout">
                <strong>{callout.title}</strong>
                <span>{callout.body}</span>
              </div>
            ))}
          </div>

          <div className="admin-accounts-config-examples">
            {COLLECTOR_SET_EXAMPLES.map((example) => (
              <div key={example.title} className="admin-accounts-config-example">
                <span>{example.title}</span>
                {example.rows.map((row) => (
                  <code key={row}>{row}</code>
                ))}
              </div>
            ))}
          </div>

          <div className="admin-accounts-config-section-list">
            {COLLECTOR_SET_FIELD_GROUPS.map((group) => (
              <section key={group.title} className="admin-accounts-config-section">
                <div className="admin-accounts-config-section-head">
                  <div>
                    <h4>{group.title}</h4>
                    <p>{group.description}</p>
                  </div>
                  {group.advanced && (
                    <span className="admin-accounts-config-tag admin-accounts-config-tag-warning">
                      Advanced
                    </span>
                  )}
                </div>

                <div className="admin-accounts-config-field-list">
                  {group.fields.map((field) => (
                    <article
                      key={field.name}
                      className="admin-accounts-config-field"
                      title={field.tooltip}
                    >
                      <div className="admin-accounts-config-field-main">
                        <div className="admin-accounts-config-field-title">
                          <strong>{field.label}</strong>
                          <code>{field.name}</code>
                        </div>
                        <p>{field.description}</p>
                      </div>

                      <div className="admin-accounts-config-field-meta">
                        {field.tags.map((tag) => (
                          <span
                            key={tag}
                            className={`admin-accounts-config-tag ${getCollectorGuideTagClass(tag)}`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className="admin-accounts-config-field-foot">
                        {field.exampleStyle === "text" ? (
                          <span>
                            {field.exampleLabel || "Example"}:{" "}
                            <span className="admin-accounts-config-field-example-text">
                              {field.example}
                            </span>
                          </span>
                        ) : (
                          <span>
                            Example: <code>{field.example}</code>
                          </span>
                        )}
                        {field.warning && <em>{field.warning}</em>}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRequestConfigModal = () => {
    const requestTypeMeta = getAccountSetRequestTypeMeta(requestConfigModal.request?.type);
    const isSetConfig = requestConfigModal.source === "set";
    const modalTitle = isSetConfig ? "Set Config" : "Requested Config";
    const modalSubject =
      requestConfigModal.accountSet?.name ||
      requestConfigModal.request?.AccountSet?.name ||
      "this account set";
    const modalDescription = isSetConfig
      ? `Live collector configs for ${modalSubject}.`
      : `${requestTypeMeta.label} for ${modalSubject}.`;
    const modalBusyId =
      requestConfigModal.request?.id ||
      requestConfigModal.accountSet?.id ||
      requestConfigModal.accountSet?._id ||
      requestConfigModal.accountSet?.setId;

    return (
      <div
        className="admin-accounts-request-modal-overlay"
        onClick={handleCloseRequestedConfig}
        role="presentation"
      >
        <div
          className="admin-accounts-request-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="requested-config-modal-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="admin-accounts-request-modal-scroll">
            <div className="admin-accounts-request-modal-header">
              <div className="admin-accounts-request-modal-heading">
                <span className="admin-accounts-request-modal-icon">
                  <ShieldCheck size={18} />
                </span>
                <div>
                  <h3 id="requested-config-modal-title">{modalTitle}</h3>
                  <p>{modalDescription}</p>
                </div>
              </div>
              <button
                type="button"
                className="admin-accounts-request-modal-close"
                onClick={handleCloseRequestedConfig}
                aria-label="Close requested config"
              >
                <X size={16} />
              </button>
            </div>

            {requestConfigModal.warning && (
              <div className="admin-accounts-request-modal-warning">
                {requestConfigModal.warning}
              </div>
            )}

            <div className="admin-accounts-request-preview-list">
              {requestConfigModal.files.map((file) => {
                const busyForFile = requestSetFileBusy?.id === modalBusyId;
                const copyAction = `copy-${file.brokerPosition}`;
                const downloadAction = `download-${file.brokerPosition}`;
                const previewRows = parseCollectorSetPreviewContent(file.content);
                const { primaryRows, secondaryRows, footerRows } =
                  splitCollectorSetPreviewRows(previewRows);
                const previewColumns =
                  secondaryRows.length > 0 ? [primaryRows, secondaryRows] : [primaryRows];

                return (
                  <section
                    key={`${file.brokerPosition}-${file.filename}`}
                    className="admin-accounts-request-preview-card"
                  >
                    <div className="admin-accounts-request-preview-card-head">
                      <div className="admin-accounts-request-preview-card-copy">
                        <div className="admin-accounts-request-preview-card-topline">
                          <span className="admin-accounts-request-preview-terminal">
                            {file.terminal || "Collector"}
                          </span>
                          {file.requestedSymbol && (
                            <span className="admin-badge admin-badge-info admin-accounts-config-info-badge admin-accounts-request-symbol-badge">
                              {file.requestedSymbol}
                            </span>
                          )}
                        </div>
                        <strong>{file.brokerName || `Broker ${file.brokerPosition}`}</strong>
                        <span>{file.filename || `broker-${file.brokerPosition}.set`}</span>
                      </div>
                      <div className="admin-accounts-request-preview-actions">
                        <button
                          type="button"
                          className="admin-btn admin-btn-primary admin-accounts-set-action-btn admin-accounts-icon-btn"
                          onClick={() => handleCopyRequestedCollectorSetFile(file)}
                          disabled={busyForFile}
                          aria-label={`Copy ${file.brokerName || file.terminal || "requested"} set file`}
                          title={
                            busyForFile && requestSetFileBusy?.action === copyAction
                              ? "Copying..."
                              : "Copy set file"
                          }
                        >
                          <Copy size={15} />
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-success admin-accounts-set-action-btn admin-accounts-icon-btn"
                          onClick={() => handleDownloadRequestedCollectorSetFile(file)}
                          disabled={busyForFile}
                          aria-label={`Download ${file.brokerName || file.terminal || "requested"} set file`}
                          title={
                            busyForFile && requestSetFileBusy?.action === downloadAction
                              ? "Preparing..."
                              : "Download set file"
                          }
                        >
                          <Download size={15} />
                        </button>
                      </div>
                    </div>

                    <div className="admin-accounts-request-preview-values">
                      <div
                        className={`admin-accounts-request-preview-columns${secondaryRows.length > 0 ? " admin-accounts-request-preview-columns-split" : ""}`}
                      >
                        {previewColumns.map((columnRows, columnIndex) => (
                          <div
                            key={`${file.brokerPosition}-${columnIndex}`}
                            className="admin-accounts-request-preview-lines"
                          >
                            {columnRows.map((row) => {
                              const isUuidValue =
                                row.type === "entry" && isCollectorSetUuidValue(row.value);
                              const PreviewFieldIcon =
                                row.type === "entry"
                                  ? getCollectorSetPreviewFieldIcon(row.key)
                                  : Info;
                              return row.type === "entry" ? (
                                <div
                                  key={row.id}
                                  className={`admin-accounts-request-preview-line${row.important ? " admin-accounts-request-preview-line-important" : ""}${isUuidValue ? " admin-accounts-request-preview-line-uuid" : ""}`}
                                >
                                  <span
                                    className="admin-accounts-request-preview-line-icon"
                                    aria-hidden="true"
                                  >
                                    <PreviewFieldIcon size={13} />
                                  </span>
                                  <span
                                    className={`admin-accounts-request-preview-line-key${row.important ? " admin-accounts-request-preview-line-key-important" : ""}`}
                                  >
                                    {row.key}
                                  </span>
                                  <span
                                    className={`admin-accounts-request-preview-line-value${row.important ? " admin-accounts-request-preview-line-value-important" : ""}${isUuidValue ? " admin-accounts-request-preview-line-value-uuid" : ""}`}
                                  >
                                    {row.value || (
                                      <span className="admin-accounts-request-preview-value-empty">
                                        Empty
                                      </span>
                                    )}
                                  </span>
                                </div>
                              ) : (
                                <div
                                  key={row.id}
                                  className="admin-accounts-request-preview-line-note"
                                >
                                  {row.text}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      {footerRows.length > 0 && (
                        <div className="admin-accounts-request-preview-footer">
                          {footerRows.map((row) => {
                            const isUuidValue =
                              row.type === "entry" && isCollectorSetUuidValue(row.value);
                            const isHelpFilters = row.type === "entry" && row.key === "HelpFilters";
                            const PreviewFieldIcon =
                              row.type === "entry"
                                ? getCollectorSetPreviewFieldIcon(row.key)
                                : Info;
                            return row.type === "entry" ? (
                              <div
                                key={row.id}
                                className={`admin-accounts-request-preview-line admin-accounts-request-preview-line-footer${row.important ? " admin-accounts-request-preview-line-important" : ""}${isUuidValue ? " admin-accounts-request-preview-line-uuid" : ""}${isHelpFilters ? " admin-accounts-request-preview-line-help" : ""}`}
                              >
                                <span
                                  className="admin-accounts-request-preview-line-icon"
                                  aria-hidden="true"
                                >
                                  <PreviewFieldIcon size={13} />
                                </span>
                                <span
                                  className={`admin-accounts-request-preview-line-key${row.important ? " admin-accounts-request-preview-line-key-important" : ""}`}
                                >
                                  {row.key}
                                </span>
                                <span
                                  className={`admin-accounts-request-preview-line-value${row.important ? " admin-accounts-request-preview-line-value-important" : ""}${isUuidValue ? " admin-accounts-request-preview-line-value-uuid" : ""}${isHelpFilters ? " admin-accounts-request-preview-line-value-help" : ""}`}
                                >
                                  {row.value || (
                                    <span className="admin-accounts-request-preview-value-empty">
                                      Empty
                                    </span>
                                  )}
                                </span>
                              </div>
                            ) : (
                              <div
                                key={row.id}
                                className="admin-accounts-request-preview-line-note"
                              >
                                {row.text}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </section>
                );
              })}
              {requestConfigModal.files.length === 0 && (
                <div className="admin-accounts-empty-table-state">
                  No requested config files available.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const setConfigGuideModal = setConfigGuideOpen ? renderSetConfigGuideModal() : null;
  const requestConfigPreviewModal = requestConfigModal.open ? renderRequestConfigModal() : null;

  return (
    <>
      <div className="admin-page admin-accounts-page">
        <div className="admin-accounts-header">
          <div>
            <h1>
              <FolderKanban className="admin-accounts-header-icon" />
              <span>Account Sets</span>
            </h1>
            <p>
              Review inbox alerts, requested account changes, and the final live collector configs.
            </p>
          </div>
          <div className="admin-page-actions">
            <button
              className="admin-btn admin-btn-primary admin-accounts-refresh-btn"
              onClick={() => loadAccounts(true)}
              disabled={loading || refreshing}
            >
              <RefreshCcw size={15} />
              {refreshing ? "Refreshing..." : "Refresh Accounts"}
            </button>
          </div>
        </div>

        {error && <div className="admin-inline-alert error">{error}</div>}

        <div className="admin-accounts-summary-grid">
          <div className="admin-accounts-summary-card">
            <div className="admin-accounts-summary-icon">
              <BellRing size={20} />
            </div>
            <div className="admin-accounts-summary-copy">
              <span>Unread Inbox</span>
              <strong>{notificationsUnread}</strong>
              <small>Recent alerts await review</small>
            </div>
          </div>
          <div className="admin-accounts-summary-card">
            <div className="admin-accounts-summary-icon">
              <ShieldCheck size={20} />
            </div>
            <div className="admin-accounts-summary-copy">
              <span>Pending Requests</span>
              <strong>{requests.filter((request) => request.status === "pending").length}</strong>
              <small>Awaiting action from admin</small>
            </div>
          </div>
          <div className="admin-accounts-summary-card">
            <div className="admin-accounts-summary-icon">
              <FolderKanban size={20} />
            </div>
            <div className="admin-accounts-summary-copy">
              <span>Active Sets</span>
              <strong>{accountSets.length}</strong>
              <small>Currently available account sets</small>
            </div>
          </div>
          <div className="admin-accounts-summary-card">
            <div className="admin-accounts-summary-icon">
              <Trash2 size={20} />
            </div>
            <div className="admin-accounts-summary-copy">
              <span>Deleted Sets</span>
              <strong>{deletedAccountSets.length}</strong>
              <small>Soft-Deleted sets await cleanup</small>
            </div>
          </div>
        </div>

        <section className="admin-card admin-accounts-card">
          <div className="admin-card-heading">
            <div>
              <div className="admin-card-title">
                <BellRing />
                <span>Inbox</span>
              </div>
              <p>
                Alert layer only. Open the related review request from here, then handle config from
                Review Requests or Set Management.
              </p>
            </div>
            <span className="admin-badge admin-badge-info">{notificationsUnread} unread</span>
          </div>

          <div className="admin-accounts-toolbar">
            <div className="admin-field-group admin-accounts-toolbar-search">
              <label className="admin-label">
                <Search size={13} />
                Search inbox notifications
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="Search inbox notifications"
                value={notificationSearch}
                onChange={(event) => setNotificationSearch(event.target.value)}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Count</label>
              <CustomSelect
                value={String(notificationPagination.pageSize)}
                onChange={(value) =>
                  setNotificationPagination({
                    pageIndex: 0,
                    pageSize: Number(value),
                  })
                }
                options={[
                  { value: "10", label: "10" },
                  { value: "50", label: "50" },
                  { value: "100", label: "100" },
                ]}
              />
            </div>
            <button
              className="admin-btn admin-btn-primary admin-accounts-action-btn"
              onClick={handleMarkAllNotificationsRead}
              disabled={notificationsUnread === 0 || actionBusy?.type === "mark-all"}
            >
              {actionBusy?.type === "mark-all" ? "Marking..." : "Mark All Read"}
            </button>
          </div>

          {notificationLoading ? (
            <>
              <div className="admin-data-table-wrap admin-accounts-table-wrap">
                <table className="admin-data-table admin-accounts-table">
                  <thead>
                    {notificationTable.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : header.column.getCanSort()
                                ? renderHeaderSort(header)
                                : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {loading
                      ? skeletonRows.map((_, index) => (
                          <tr key={`notif-skeleton-${index}`} className="admin-table-skeleton-row">
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-sm" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-sm" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-md" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-md" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-md" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-lg" />
                            </td>
                            <td>
                              <div className="admin-table-skeleton-actions">
                                <span className="admin-skeleton-line admin-skeleton-line-btn" />
                                <span className="admin-skeleton-line admin-skeleton-line-btn" />
                              </div>
                            </td>
                          </tr>
                        ))
                      : notificationRows.map((row) => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
              {!notificationLoading && (
                <div className="admin-table-footer admin-accounts-table-footer">
                  <span className="admin-table-summary">
                    Showing {notificationRowStart}-{notificationRowEnd} of {notificationTotal}
                  </span>
                  <div className="admin-table-pagination">
                    <button
                      className="admin-btn admin-btn-ghost"
                      onClick={() =>
                        setNotificationPagination((prev) => ({
                          ...prev,
                          pageIndex: Math.max(0, prev.pageIndex - 1),
                        }))
                      }
                      disabled={notificationPagination.pageIndex === 0}
                    >
                      <ChevronLeft size={15} />
                      Prev
                    </button>
                    <span className="admin-accounts-pagecount">
                      Page {notificationPagination.pageIndex + 1} / {notificationTotalPages}
                    </span>
                    <button
                      className="admin-btn admin-btn-ghost"
                      onClick={() =>
                        setNotificationPagination((prev) => ({
                          ...prev,
                          pageIndex: Math.min(notificationTotalPages - 1, prev.pageIndex + 1),
                        }))
                      }
                      disabled={notificationPagination.pageIndex >= notificationTotalPages - 1}
                    >
                      Next
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="admin-data-table-wrap admin-accounts-table-wrap">
                <table className="admin-data-table admin-accounts-table">
                  <thead>
                    {notificationTable.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : header.column.getCanSort()
                                ? renderHeaderSort(header)
                                : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {notificationRows.length > 0 ? (
                      notificationRows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr className="admin-accounts-empty-table-row">
                        <td
                          colSpan={notificationTable.getVisibleLeafColumns().length}
                          className="admin-accounts-empty-table-cell"
                        >
                          <div className="admin-accounts-empty-table-state">
                            No inbox notifications.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {notificationTotal > 0 && (
                <div className="admin-table-footer admin-accounts-table-footer">
                  <span className="admin-table-summary">
                    Showing {notificationRowStart}-{notificationRowEnd} of {notificationTotal}
                  </span>
                  <div className="admin-table-pagination">
                    <button
                      className="admin-btn admin-btn-ghost"
                      onClick={() =>
                        setNotificationPagination((prev) => ({
                          ...prev,
                          pageIndex: Math.max(0, prev.pageIndex - 1),
                        }))
                      }
                      disabled={notificationPagination.pageIndex === 0}
                    >
                      <ChevronLeft size={15} />
                      Prev
                    </button>
                    <span className="admin-accounts-pagecount">
                      Page {notificationPagination.pageIndex + 1} / {notificationTotalPages}
                    </span>
                    <button
                      className="admin-btn admin-btn-ghost"
                      onClick={() =>
                        setNotificationPagination((prev) => ({
                          ...prev,
                          pageIndex: Math.min(notificationTotalPages - 1, prev.pageIndex + 1),
                        }))
                      }
                      disabled={notificationPagination.pageIndex >= notificationTotalPages - 1}
                    >
                      Next
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <section ref={reviewSectionRef} className="admin-card admin-accounts-card">
          <div className="admin-card-heading">
            <div>
              <div className="admin-card-title">
                <ShieldCheck />
                <span>Review Requests</span>
              </div>
              <p>Primary approval surface for link, modify, and delete requests.</p>
            </div>
          </div>

          <div className="admin-accounts-toolbar">
            <div className="admin-field-group admin-accounts-toolbar-search">
              <label className="admin-label">
                <Search size={13} />
                Search requests
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="Search requests"
                value={requestSearch}
                onChange={(event) => setRequestSearch(event.target.value)}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">
                <ShieldCheck size={13} />
                Filter
              </label>
              <CustomSelect
                value={requestFilter}
                onChange={setRequestFilter}
                options={[
                  { value: "pending", label: "Pending" },
                  { value: "approved", label: "Approved" },
                  { value: "rejected", label: "Rejected" },
                  { value: "all", label: "All" },
                ]}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Count</label>
              <CustomSelect
                value={String(requestPagination.pageSize)}
                onChange={(value) => requestTable.setPageSize(Number(value))}
                options={[
                  { value: "10", label: "10" },
                  { value: "50", label: "50" },
                  { value: "100", label: "100" },
                ]}
              />
            </div>
          </div>

          {loading ? (
            <>
              <div className="admin-data-table-wrap admin-accounts-table-wrap">
                <table className="admin-data-table admin-accounts-table">
                  <thead>
                    {requestTable.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : header.column.getCanSort()
                                ? renderHeaderSort(header)
                                : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {loading
                      ? skeletonRows.map((_, index) => (
                          <tr
                            key={`request-skeleton-${index}`}
                            className="admin-table-skeleton-row"
                          >
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-sm" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-sm" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-sm" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-md" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-md" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-md" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-lg" />
                            </td>
                            <td>
                              <span className="admin-skeleton-line admin-skeleton-line-lg" />
                            </td>
                            <td>
                              <div className="admin-table-skeleton-actions">
                                <span className="admin-skeleton-line admin-skeleton-line-icon" />
                                <span className="admin-skeleton-line admin-skeleton-line-icon" />
                              </div>
                            </td>
                          </tr>
                        ))
                      : requestRows.map((row) => (
                          <tr
                            key={row.id}
                            ref={(node) => {
                              if (node) requestRowRefs.current.set(String(row.original.id), node);
                              else requestRowRefs.current.delete(String(row.original.id));
                            }}
                            className={
                              focusedRequestId &&
                              String(focusedRequestId) === String(row.original.id)
                                ? "admin-accounts-row-focus"
                                : ""
                            }
                          >
                            {row.getVisibleCells().map((cell) => (
                              <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
              {!loading && (
                <div className="admin-table-footer admin-accounts-table-footer">
                  <span className="admin-table-summary">
                    Showing {requestRowStart}-{requestRowEnd} of {filteredRequests.length}
                  </span>
                  <div className="admin-table-pagination">
                    <button
                      className="admin-btn admin-btn-ghost"
                      onClick={() => requestTable.previousPage()}
                      disabled={!requestTable.getCanPreviousPage()}
                    >
                      <ChevronLeft size={15} />
                      Prev
                    </button>
                    <span className="admin-accounts-pagecount">
                      Page {requestPagination.pageIndex + 1} / {requestTable.getPageCount() || 1}
                    </span>
                    <button
                      className="admin-btn admin-btn-ghost"
                      onClick={() => requestTable.nextPage()}
                      disabled={!requestTable.getCanNextPage()}
                    >
                      Next
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="admin-data-table-wrap admin-accounts-table-wrap">
                <table className="admin-data-table admin-accounts-table">
                  <thead>
                    {requestTable.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : header.column.getCanSort()
                                ? renderHeaderSort(header)
                                : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {requestRows.length > 0 ? (
                      requestRows.map((row) => (
                        <tr
                          key={row.id}
                          ref={(node) => {
                            if (node) requestRowRefs.current.set(String(row.original.id), node);
                            else requestRowRefs.current.delete(String(row.original.id));
                          }}
                          className={
                            focusedRequestId && String(focusedRequestId) === String(row.original.id)
                              ? "admin-accounts-row-focus"
                              : ""
                          }
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr className="admin-accounts-empty-table-row">
                        <td
                          colSpan={requestTable.getVisibleLeafColumns().length}
                          className="admin-accounts-empty-table-cell"
                        >
                          <div className="admin-accounts-empty-table-state">No requests found.</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filteredRequests.length > 0 && (
                <div className="admin-table-footer admin-accounts-table-footer">
                  <span className="admin-table-summary">
                    Showing {requestRowStart}-{requestRowEnd} of {filteredRequests.length}
                  </span>
                  <div className="admin-table-pagination">
                    <button
                      className="admin-btn admin-btn-ghost"
                      onClick={() => requestTable.previousPage()}
                      disabled={!requestTable.getCanPreviousPage()}
                    >
                      <ChevronLeft size={15} />
                      Prev
                    </button>
                    <span className="admin-accounts-pagecount">
                      Page {requestPagination.pageIndex + 1} / {requestTable.getPageCount() || 1}
                    </span>
                    <button
                      className="admin-btn admin-btn-ghost"
                      onClick={() => requestTable.nextPage()}
                      disabled={!requestTable.getCanNextPage()}
                    >
                      Next
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <section className="admin-card admin-accounts-card">
          <div className="admin-card-heading">
            <div>
              <div className="admin-card-title">
                <FolderKanban />
                <span>Set Management</span>
              </div>
              <p>Final live config home for approved account sets, plus deleted-set cleanup.</p>
            </div>
            <button
              type="button"
              className="admin-badge admin-badge-info admin-accounts-config-info-badge"
              onClick={() => setSetConfigGuideOpen(true)}
              title="Open MT4 and MT5 set-file field guide"
              aria-label="Open MT4 and MT5 set-file field guide"
            >
              <Info size={12} />
              Guide
            </button>
          </div>

          <div className="admin-accounts-toolbar">
            <div className="admin-field-group admin-accounts-toolbar-search">
              <label className="admin-label">
                <Search size={13} />
                Search account sets
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="Search account sets"
                value={accountSetSearch}
                onChange={(event) => setAccountSetSearch(event.target.value)}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Status</label>
              <CustomSelect
                value={accountSetFilter}
                onChange={setAccountSetFilter}
                options={[
                  { value: "all", label: "All" },
                  { value: "active", label: "Active" },
                  { value: "reconnect_required", label: "Reconnect Required" },
                  { value: "pending_review", label: "Pending Review" },
                  { value: "draft", label: "Draft" },
                  { value: "deleted", label: "Deleted" },
                ]}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Count</label>
              <CustomSelect
                value={String(accountSetPagination.pageSize)}
                onChange={(value) => accountSetTable.setPageSize(Number(value))}
                options={[
                  { value: "10", label: "10" },
                  { value: "50", label: "50" },
                  { value: "100", label: "100" },
                ]}
              />
            </div>
          </div>

          <div className="admin-data-table-wrap admin-accounts-table-wrap admin-accounts-set-table-wrap">
            <table className="admin-data-table admin-accounts-table admin-accounts-set-table">
              <thead>
                {accountSetTable.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : header.column.getCanSort()
                            ? renderHeaderSort(header)
                            : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  skeletonRows.map((_, index) => (
                    <tr key={`accountset-skeleton-${index}`} className="admin-table-skeleton-row">
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-btn" />
                      </td>
                    </tr>
                  ))
                ) : accountSetRows.length > 0 ? (
                  accountSetRows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className="admin-accounts-empty-table-row">
                    <td
                      colSpan={accountSetTable.getVisibleLeafColumns().length}
                      className="admin-accounts-empty-table-cell"
                    >
                      <div className="admin-accounts-empty-table-state">
                        {accountSetFilter === "active"
                          ? "No active account sets found."
                          : accountSetFilter === "reconnect_required"
                            ? "No reconnect-required account sets found."
                            : accountSetFilter === "pending_review"
                              ? "No pending-review account sets found."
                              : accountSetFilter === "draft"
                                ? "No draft account sets found."
                                : accountSetFilter === "deleted"
                                  ? "No deleted account sets found."
                                  : "No account sets found."}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && filteredAccountSetRows.length > 0 ? (
            <div className="admin-table-footer admin-accounts-table-footer">
              <span className="admin-table-summary">
                Showing {accountSetRowStart}-{accountSetRowEnd} of {filteredAccountSetRows.length}
              </span>
              <div className="admin-table-pagination">
                <button
                  className="admin-btn admin-btn-ghost"
                  onClick={() => accountSetTable.previousPage()}
                  disabled={!accountSetTable.getCanPreviousPage()}
                >
                  <ChevronLeft size={15} />
                  Prev
                </button>
                <span className="admin-accounts-pagecount">
                  Page {accountSetPagination.pageIndex + 1} / {accountSetTable.getPageCount() || 1}
                </span>
                <button
                  className="admin-btn admin-btn-ghost"
                  onClick={() => accountSetTable.nextPage()}
                  disabled={!accountSetTable.getCanNextPage()}
                >
                  Next
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      {setConfigGuideModal &&
        (typeof document === "undefined"
          ? setConfigGuideModal
          : createPortal(setConfigGuideModal, document.body))}

      {requestConfigPreviewModal &&
        (typeof document === "undefined"
          ? requestConfigPreviewModal
          : createPortal(requestConfigPreviewModal, document.body))}

      <PageModal
        open={Boolean(deleteNotificationTarget)}
        onClose={() => setDeleteNotificationTarget(null)}
        onConfirm={handleDeleteNotification}
        title="Delete this notification?"
        description={`This removes the inbox notification for ${deleteNotificationTarget?.payload?.accountSet?.name || deleteNotificationTarget?.accountSet?.name || "this account set"}.`}
        confirmLabel={
          actionBusy?.type === "delete-notification" ? "Deleting..." : "Delete notification"
        }
        loading={actionBusy?.type === "delete-notification"}
        tone="danger"
      />

      <PageModal
        open={Boolean(deleteAccountSetId)}
        onClose={() => setDeleteAccountSetId(null)}
        onConfirm={handlePermanentDelete}
        title="Permanently delete this account set?"
        description="This removes the deleted account set forever. The action cannot be undone."
        confirmLabel={
          actionBusy?.type === "permanent-delete" ? "Deleting..." : "Permanently delete"
        }
        loading={actionBusy?.type === "permanent-delete"}
        tone="danger"
      />
    </>
  );
}
